// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import type { ShippingCarrier } from '../../api/client'
import type { CarrierAccountInput } from '../../api/shipping'
import { friendlyError } from '../../api/errors'
import { useCarrierServices, useCreateCarrierAccount, useUpdateCarrierAccount } from '../../hooks/queries/shipping'
import { carrierLabel, carrierSetup } from '../../lib/carriers'
import AppButton from '../ui/AppButton'
import AppDialog from '../ui/AppDialog'
import { AppTextField, CheckboxField, ChoiceToggle, SelectField, ToggleSwitch } from '../ui/FormFields'
import Notice from '../ui/Notice'
import { useToast } from '../ui/Toast'
import PickupLocationField from './PickupLocationField'

type Env = 'test' | 'live'

interface Form {
  carrier: string
  display_name: string
  env: Env
  base_url: string
  api_website: string
  account_number: string
  default_service_level: string
  credentials: Record<string, string>
  default_cold_chain: boolean
  default_signature: boolean
  require_quote_confirm: boolean
  send_cod: boolean
  is_active: boolean
}

const NO_DESTINATION = {}

const fromAccount = (c: ShippingCarrier): Form => ({
  carrier: c.carrier, display_name: c.display_name, env: c.is_test_mode ? 'test' : 'live',
  base_url: c.base_url ?? '', api_website: c.api_website ?? '', account_number: c.account_number ?? '',
  default_service_level: c.default_service_level ?? '', credentials: {},
  default_cold_chain: c.default_cold_chain, default_signature: c.default_signature,
  require_quote_confirm: c.require_quote_confirm !== false, send_cod: c.send_cod === true, is_active: c.is_active,
})

const blank = (carrier: string): Form => ({
  carrier, display_name: carrierLabel(carrier), env: 'test', base_url: carrierSetup(carrier).baseUrls?.test ?? '',
  api_website: '', account_number: '', default_service_level: '', credentials: {},
  default_cold_chain: false, default_signature: false, require_quote_confirm: true, send_cod: false, is_active: true,
})

const orUndefined = (v: string) => v.trim() || undefined

/** Only typed secrets are sent; a blank field keeps the stored one. */
const secrets = (typed: Record<string, string>) => {
  const filled = Object.entries(typed).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()] as const)
  return filled.length ? Object.fromEntries(filled) : undefined
}

/**
 * D-SHIP-23 §10 — add or configure one shipping provider account for a pharmacy. Secrets are
 * write-only: a blank field keeps what is stored.
 */
export default function CarrierAccountDialog({ account, carriers, pharmacyId, onClose }: {
  /** null = add a new provider. */
  account: ShippingCarrier | null
  carriers: string[]
  pharmacyId?: string
  onClose: () => void
}) {
  const toast = useToast()
  const [form, setForm] = useState<Form>(() => account ? fromAccount(account) : blank(carriers[0] ?? 'manual'))
  const create = useCreateCarrierAccount(pharmacyId)
  const update = useUpdateCarrierAccount(pharmacyId)
  const busy = create.isPending || update.isPending
  const error = create.error ?? update.error
  const setup = carrierSetup(form.carrier)
  const services = useCarrierServices(account?.id, NO_DESTINATION, pharmacyId)
  const set = (patch: Partial<Form>) => setForm(f => ({ ...f, ...patch }))

  const switchEnv = (env: Env) => set({ env, ...(setup.baseUrls ? { base_url: setup.baseUrls[env] } : {}) })

  const input = (): CarrierAccountInput => ({
    display_name: form.display_name.trim(),
    base_url: orUndefined(form.base_url),
    api_website: setup.usesSite ? orUndefined(form.api_website) : undefined,
    account_number: orUndefined(form.account_number),
    default_service_level: orUndefined(form.default_service_level),
    credentials: secrets(form.credentials),
    is_test_mode: form.env === 'test',
    default_cold_chain: form.default_cold_chain,
    default_signature: form.default_signature,
    require_quote_confirm: form.require_quote_confirm,
    send_cod: form.send_cod,
    is_active: form.is_active,
  })

  const done = (message: string) => { toast.show(message, 'success'); onClose() }

  const save = () => {
    if (account) {
      update.mutate({ id: account.id, input: input() }, { onSuccess: () => done('Provider saved.') })
      return
    }
    // Create takes the connection fields; quote and COD defaults are set right after.
    create.mutate({ ...input(), carrier: form.carrier }, {
      onSuccess: created => update.mutate(
        { id: created.id, input: { require_quote_confirm: form.require_quote_confirm, send_cod: form.send_cod } },
        { onSuccess: () => done(created.account_number ? 'Provider added. Its only branch was selected automatically.' : 'Provider added.') }),
    })
  }

  const serviceOptions = services.data?.options.map(o => ({ value: o.code, label: o.name })) ?? []
  const stored = new Set(account?.credential_keys ?? [])

  return (
    <AppDialog open onClose={onClose} busy={busy} maxWidth="md"
      title={account ? `Configure ${account.display_name}` : 'Add shipping provider'}
      actions={<>
        <AppButton onClick={onClose} disabled={busy}>Cancel</AppButton>
        <AppButton variant="contained" disabled={busy || !form.display_name.trim()} onClick={save}>
          {busy ? 'Saving…' : account ? 'Save' : 'Add provider'}
        </AppButton>
      </>}>
      {error && <Notice severity="error">{friendlyError(error)}</Notice>}
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {account
            ? <AppTextField size="small" fullWidth label="Provider" value={carrierLabel(form.carrier)} InputProps={{ readOnly: true }} />
            : <SelectField size="small" fullWidth label="Provider" value={form.carrier}
                onChange={c => setForm(f => ({ ...blank(c), display_name: f.display_name === carrierLabel(f.carrier) ? carrierLabel(c) : f.display_name }))}
                options={carriers.map(c => ({ value: c, label: carrierLabel(c) }))} />}
          <AppTextField size="small" fullWidth required label="Display name" value={form.display_name}
            inputProps={{ maxLength: 80 }} onChange={e => set({ display_name: e.target.value })} />
        </Stack>

        {form.carrier !== 'manual' && <>
          <Box>
            <Box sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>Environment</Box>
            <ChoiceToggle ariaLabel="Environment" value={form.env} onChange={switchEnv}
              options={[{ value: 'test', label: 'Test' }, { value: 'live', label: 'Live' }]} />
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <AppTextField size="small" fullWidth label="API base URL" value={form.base_url}
              inputProps={{ maxLength: 256 }} onChange={e => set({ base_url: e.target.value })} />
            {setup.usesSite && <AppTextField size="small" fullWidth label="Site code" value={form.api_website}
              inputProps={{ maxLength: 128 }} onChange={e => set({ api_website: e.target.value })} />}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {setup.credentials.map(f => (
              <AppTextField key={f.key} size="small" fullWidth label={f.label}
                type={f.secret ? 'password' : 'text'} autoComplete={f.secret ? 'new-password' : 'off'}
                placeholder={stored.has(f.key) ? 'Stored — leave blank to keep' : ''}
                helperText={stored.has(f.key) ? 'On file' : 'Not set'}
                value={form.credentials[f.key] ?? ''}
                onChange={e => set({ credentials: { ...form.credentials, [f.key]: e.target.value } })} />
            ))}
          </Stack>
        </>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {setup.pickupFromCarrier
            ? account
              ? <PickupLocationField carrierId={account.id} pharmacyId={pharmacyId} label={setup.accountLabel}
                  value={form.account_number} onChange={id => set({ account_number: id })} />
              : <AppTextField size="small" fullWidth label={setup.accountLabel} value="" disabled
                  helperText="Read from the carrier after the provider is added." />
            : <AppTextField size="small" fullWidth label={setup.accountLabel} value={form.account_number}
                inputProps={{ maxLength: 64 }} onChange={e => set({ account_number: e.target.value })} />}
          {serviceOptions.length > 0
            ? <SelectField size="small" fullWidth label="Default service" value={form.default_service_level}
                onChange={v => set({ default_service_level: v })}
                options={[{ value: '', label: 'Carrier default' }, ...serviceOptions]} />
            : <AppTextField size="small" fullWidth label="Default service" value={form.default_service_level}
                inputProps={{ maxLength: 64 }} onChange={e => set({ default_service_level: e.target.value })} />}
        </Stack>

        <Box>
          <ToggleSwitch label="Enabled for this pharmacy" checked={form.is_active} onChange={v => set({ is_active: v })} />
          <CheckboxField label="Default cold chain" checked={form.default_cold_chain} onChange={v => set({ default_cold_chain: v })} />
          <CheckboxField label="Default signature" checked={form.default_signature} onChange={v => set({ default_signature: v })} />
          <CheckboxField label="Quote before buy" checked={form.require_quote_confirm} onChange={v => set({ require_quote_confirm: v })} />
          <CheckboxField label="Send copay to courier as COD" checked={form.send_cod} onChange={v => set({ send_cod: v })} />
        </Box>
        {form.send_cod && <Notice severity="warning">Every new order will carry a COD amount the driver collects. Turning it off takes effect on the next order.</Notice>}
      </Stack>
    </AppDialog>
  )
}
